// ABOUTME: Modal for filing a shipper claim (investigation) on a shipped order
// ABOUTME: Collects the reason and the claimed orderlines with their value, then mails the claim

import { Box, InputBase, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import { useTranslation } from "react-i18next";
import { InputDropdown } from "../../theme/dropdowns/InputDropdown";
import { useCreateClaimMutation } from "../../../newapi/claim/claimSlice";
import { showToastMessagePostRequest, showToastMessage, showToastMessageRequestError } from "../../../utils/toasts";
import { general_states } from "../../../utils/staticEnums";

// The rate to fall back on when a line carries no VAT of its own.
const DEFAULT_VAT_PERCENTAGE = 21;

// Mirrors the backend: a shipper cannot act on a two-character problem description.
const MIN_REASON_LENGTH = 5;

// The Dutch VAT rates: zero-rated, reduced and standard.
const VAT_OPTIONS = [0, 9, 21].map(percentage => ({ name: `${percentage}%`, value: percentage }));

// A claim can never cover more units than the order line held.
const amountOptions = (max) => Array.from({ length: (max ?? 0) + 1 }, (_, value) => ({ name: `${value}`, value }));

// Only standard, active shipments can be claimed on — a returned or cancelled parcel is not
// something the shipper investigates.
export const claimableShipments = (order) => (order?.shipments ?? [])
    .filter(shipment => shipment.type === "standard" && shipment.state === general_states.ACTIVE);

const MAddClaim = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const shipments = claimableShipments(props.data);
    const [shipmentId, setShipmentId] = useState(null);
    const [reason, setReason] = useState("");
    const [lines, setLines] = useState([]);

    useEffect(() => {
        setShipmentId(shipments[0]?.id ?? null);
    }, [props.data?.id, shipments.length]);

    useEffect(() => {
        // sellingPriceExVAT is the value of the whole line, so divide it back out per unit.
        setLines((props.orderLines ?? []).filter(x => x.type !== "add_on").map(line => {
            const amount = line.amount ?? 0;
            const lineValue = parseFloat(line.sellingPriceExVAT ?? 0);
            const unitPrice = amount > 0 && lineValue > 0 ? lineValue / amount : 0;

            return {
                orderLineId: line.id,
                description: line.product?.description ?? "",
                sku: line.product?.sku ?? "",
                amount: amount,
                maxAmount: amount,
                unitPriceExVat: unitPrice ? unitPrice.toFixed(2) : "0.00",
                vatPercentage: DEFAULT_VAT_PERCENTAGE
            };
        }));
    }, [props.orderLines]);

    const handleClose = () => {
        setReason("");
        props.handleClose();
    };

    const handleLineChange = (index, key, value) => {
        setLines(lines.map((line, i) => i === index ? { ...line, [key]: value } : line));
    };

    const [createClaim, { isLoading }] = useCreateClaimMutation();

    const saveClaim = async () => {
        try {
            if (isLoading) return;

            if (!reason || reason.trim().length === 0) {
                showToastMessage("error", t("claim_reason_required"));
                return;
            }
            if (reason.trim().length < MIN_REASON_LENGTH) {
                showToastMessage("error", t("claim_reason_too_short", { count: MIN_REASON_LENGTH }));
                return;
            }
            if (!shipmentId) {
                showToastMessage("error", t("claim_shipment_required"));
                return;
            }

            const data = await createClaim({
                orderId: props.data?.id,
                shipmentId: shipmentId,
                reason: reason,
                lines: lines
                    .filter(line => parseInt(line.amount) > 0)
                    .map(line => ({
                        orderLineId: line.orderLineId,
                        description: line.description,
                        sku: line.sku,
                        amount: parseInt(line.amount) || 0,
                        unitPriceExVat: parseFloat(line.unitPriceExVat) || 0,
                        vatPercentage: parseFloat(line.vatPercentage) || 0
                    }))
            }).unwrap();

            showToastMessagePostRequest(t, data);
            handleClose();
        } catch (err) {
            showToastMessageRequestError(t("create_failed"), t, err);
        }
    };

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const inputBox = {
        display: "flex",
        height: "40px",
        borderRadius: "20px",
        backgroundColor: colors.grey[100],
        marginTop: 0.5,
        marginBottom: 0.5,
    };

    return (
        <SHModal
            open={props.open}
            onClose={handleClose}
        >
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1
                }}>
                <Subheading>{t("create_claim")}</Subheading>
            </Box>

            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    paddingX: 3,
                    paddingY: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    maxHeight: "60vh",
                    overflowY: "auto"
                }}
            >
                {/* Only ask which parcel when the order actually went out in more than one. */}
                {shipments.length > 1 &&
                    <Box>
                        <Text>{t("shipment")}</Text>
                        <Box sx={{ marginTop: 0.5 }}>
                            <InputDropdown
                                displayName={"trackingNumber"}
                                onChange={(selected) => setShipmentId(shipments[selected]?.id ?? null)}
                                noSelection={t("choose_option")}
                                options={shipments}
                                selected={shipments.findIndex(shipment => shipment.id === shipmentId)}
                                closeOnSelection
                            />
                        </Box>
                    </Box>
                }

                <Box>
                    <Text>{t("reason")}</Text>
                    <Box sx={{ ...inputBox, height: "80px" }}>
                        <InputBase
                            multiline
                            rows={3}
                            sx={{ px: 2, py: 1, flex: 1, color: colors.txt["primary"] }}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 1, paddingTop: 1 }}>
                    <Box sx={{ flex: 2 }}><Text bold>{t("product")}</Text></Box>
                    <Box sx={{ width: 96 }}><Text bold>{t("amount")}</Text></Box>
                    <Box sx={{ width: 120 }}><Text bold>{t("price_per_unit_excl_vat")}</Text></Box>
                    <Box sx={{ width: 96 }}><Text bold>{t("vat")}</Text></Box>
                </Box>

                {lines.map((line, index) => (
                    <Box key={`claim-line-${line.orderLineId}`} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Box sx={{ flex: 2, display: "flex", flexDirection: "column" }}>
                            <Text>{line.description}</Text>
                            <Text light>{line.sku}</Text>
                        </Box>
                        <Box sx={{ width: 96 }}>
                            <InputDropdown
                                displayName={"name"}
                                options={amountOptions(line.maxAmount)}
                                selected={line.amount}
                                onChange={(selected) => handleLineChange(index, 'amount', amountOptions(line.maxAmount)[selected]?.value ?? 0)}
                                closeOnSelection
                            />
                        </Box>
                        <Box sx={{ ...inputBox, width: 120 }}>
                            <InputBase
                                sx={{ px: 2, flex: 1, color: colors.txt["primary"] }}
                                value={line.unitPriceExVat}
                                onChange={(e) => handleLineChange(index, 'unitPriceExVat', e.target.value)}
                            />
                        </Box>
                        <Box sx={{ width: 96 }}>
                            <InputDropdown
                                displayName={"name"}
                                options={VAT_OPTIONS}
                                selected={VAT_OPTIONS.findIndex(option => option.value === Number(line.vatPercentage))}
                                onChange={(selected) => handleLineChange(index, 'vatPercentage', VAT_OPTIONS[selected]?.value ?? 0)}
                                closeOnSelection
                            />
                        </Box>
                    </Box>
                ))}
            </Box>

            <Box
                component="footer"
                sx={{
                    pt: 2,
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingRight: 3,
                    zIndex: 1058,
                    position: "relative",
                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    "@media screen and (max-width: 47.9375em)": {
                        paddingRight: 0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon />}>{t("cancel")}</Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton isLoading={isLoading} className={"group-btn"} color={"green"}
                        variant="contained"
                        onClick={saveClaim}
                    >{t("create_claim")}</Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    )
}

export default MAddClaim;
